import { Fragment } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset } from '@opallabs/kuiper'
import { AccountName } from '@opallabs/periodic/src/AccountName'
import { ContentAvatar } from '@opallabs/periodic/src/ContentAvatar'
import { ContentTextInput } from '@opallabs/periodic/src/ContentTextInput'
import { ScheduledTime } from '@opallabs/periodic/src/ScheduledTime'
import { SingleAssetPicker } from '@opallabs/periodic/src/SingleAssetPicker'
import { doNothing } from '../../../helpers/functions/doNothing'
import { OnShowAssetPickerProps } from '../../../helpers/types'
import { styles, textColors } from './styles'

export type MomentHeaderProps = {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  asset: AssetReference | null
  description: string
  display: string
  isPresenting: boolean
  onDescriptionChange: (val: string) => void
  onDragDrop?: (e: React.DragEvent<HTMLDivElement>) => void
  onExpandAsset?: (assetId: number) => void
  onRemoveAsset?: (assetId: number) => void
  onShowAssetPicker?: (args: OnShowAssetPickerProps) => void
  onTitleChange: (val: string) => void
  postType: string
  readOnly: boolean
  scheduledAt: string
  timezone: string
  title: string
}

export const TwitterMomentHeader: React.FC<MomentHeaderProps> = ({
  accountImage,
  accountName,
  accountUsername,
  asset,
  description,
  display,
  isPresenting,
  onDescriptionChange,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  onTitleChange,
  postType,
  readOnly,
  scheduledAt,
  timezone,
  title,
}) => {
  const isMobile = display.includes('mobile')

  const handleExpandAsset = () => {
    if (!onExpandAsset || !asset) return
    onExpandAsset(asset.id || 0)
  }

  const handleRemoveAsset = () => {
    if (!onRemoveAsset || !asset) return
    onRemoveAsset(asset.id || 0)
  }

  return (
    <Fragment>
      <div>
        {asset ? (
          <Asset
            hasAssetOverlay={asset && (!readOnly || isPresenting)}
            hasAssetOptions={false}
            readOnly={readOnly}
            removeable={!isPresenting}
            overrideStyles={{
              assetBase: styles.getAssetStyles(display),
            }}
            assetReference={asset}
            onEmbiggen={handleExpandAsset}
            onRemove={handleRemoveAsset}
          />
        ) : (
          <SingleAssetPicker
            allowedUploadTypes={'image'}
            postType={postType}
            style={styles.getAssetStyles(display)}
            onDragDrop={(e) => onDragDrop && onDragDrop(e)}
            onShowAssetPicker={
              onShowAssetPicker
                ? () =>
                    onShowAssetPicker({ destination: 'moment_header_asset' })
                : doNothing
            }
            readOnly={readOnly}
          />
        )}
      </div>
      <div css={styles.getHeaderWrapperStyles(isMobile)}>
        <div css={styles.accountWrapper}>
          <ContentAvatar
            assetReference={accountImage}
            size={isMobile ? 49 : 24}
          />
          <div css={styles.getAccountStyles(isMobile)}>
            <AccountName
              name={accountName}
              size={16}
              color={textColors.darkerGray}
              style={styles.accountName}
            />
            {accountName && (
              <div css={styles.getAccountUsername(isMobile)}>
                <AccountName
                  name={accountUsername}
                  size={15}
                  color={textColors.lightGray}
                  accountHandle="@"
                />
              </div>
            )}
          </div>
        </div>
        <div css={styles.getScheduledTimeStyles(isMobile)}>
          <ScheduledTime
            color={textColors.medGray}
            fontSize={16}
            timestamp={scheduledAt}
            timezone={timezone}
            dateFormat="MMM d, yyyy"
          />
        </div>
        <ContentTextInput
          ariaLabel="Content title"
          value={title}
          style={styles.title}
          onChange={onTitleChange}
          readOnly={readOnly}
          characterLimit={160}
          placeholder="Add title..."
        />
        <ContentTextInput
          ariaLabel="Content description"
          value={description}
          style={styles.description}
          onChange={onDescriptionChange}
          highlightSocialTags={true}
          readOnly={readOnly}
          characterLimit={600}
          placeholder="Add description..."
        />
      </div>
    </Fragment>
  )
}
